import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const SriLankaPage = () => {
  const sriLankaData = {
    name: 'Sri Lanka',
    region: 'South Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Sri Lanka | NMRA | CTD-Ready Dossiers',
    metaDescription: 'Discover how to register pharmaceutical and nutritional supplement products in Sri Lanka. NMRA requirements, CTD dossier process, supplement classification, regulatory insights for a South-Asian island market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Sri Lanka',
      subtitle: 'An import-dependent island market with emerging regulatory maturity via NMRA, growing NCD burden and structured pathways for pharmaceuticals and supplements.',
      cta: 'Start Your Sri Lanka Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~22M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure (% of GDP)', value: '~3.8-4.0%' },
      { label: 'Import Reliance (Pharma)', value: 'High (limited local mfg)' },
      { label: 'Languages', value: 'Sinhala, Tamil, English' },
      { label: 'Regulatory Authority', value: 'NMRA' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Import-Driven & Reform-Focused',
      stats: [
        'All medicines for import/manufacture must be registered via NMRA',
        'Regulated under National Medicines Regulatory Authority Act No. 05 of 2015',
        'NMRA oversees manufacturing/import licences, distribution control, pricing',
        'Nutraceuticals/food supplements regulated under "borderline products" classification',
        'Preliminary classification by NMRA required for supplements',
        'Increasing demand for metabolic, cardiovascular and wellness supplements',
        'Essential medicines and paediatric products remain relevant'
      ],
      opportunities: [
        'Import-oriented medicine supply creates opportunity',
        'Growing chronic disease burden supports nutraceutical demand',
        'Regulatory reform via NMRA improving predictability',
        'English language acceptance simplifies documentation for Indian exporters'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Landscape: Dual Pressure from NCDs + Import Gaps',
      subtitle: 'Sri Lanka is transitioning from infectious disease dominance to growing NCD burden, creating diverse opportunities for pharma and nutraceuticals.',
      categories: [
        {
          name: 'Non-Communicable Diseases (NCDs) — Growing Driver',
          items: [
            'Rising prevalence of diabetes, hypertension and cardiovascular disease',
            'Shift away from infectious disease is ongoing',
            'Growth in overweight/obesity and metabolic health demands',
            'Supporting the nutraceutical segment expansion',
            'Pharma: Antihypertensives, antidiabetics, lipid-lowering agents, cardiology products',
            'Nutra: Metabolic & glycaemic support, cardiovascular health, antioxidant blends, joint/ageing support'
          ]
        },
        {
          name: 'Infectious Diseases & Nutrition',
          items: [
            'Infectious disease profile milder vs some peer markets',
            'Imported essential medicines remain relevant',
            'Paediatric products in steady demand',
            'Nutritional supplements (iron/folate) important',
            'Clear opportunity in maternal/child health niches',
            'Portfolio: Essential generics, paediatric antibiotics, prenatal vitamins, immune blends'
          ]
        },
        {
          name: 'Best-Fit Portfolio Categories',
          items: [
            'Pharma: Antihypertensives, antidiabetics, lipid-lowering agents, paediatric antibiotics, essential generics',
            'Nutra: Metabolic support, cardiovascular wellness, prenatal & women\'s health, immune/vitamin blends'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'National Medicines Regulatory Authority (NMRA)',
      description: 'NMRA regulates medicines, medical devices and borderline products (food supplements) under the NMRA Act No. 05 of 2015. Medicines registration and import done via Medicines Evaluation Committee (MEC) with administrative processes.',
      pharma: {
        timeline: 'Varies based on dossier quality and MEC evaluation process',
        requirements: [
          'Manufacturing site approval or import-licence setup for foreign manufacturer',
          'Marketing Authorisation Application (MAA) via Form A (Schedule IV)',
          'Dossier with manufacturing licence, GMP certificate, COA, stability data',
          'Analytical methods and clinical/efficacy summaries where required',
          'Samples for import clearance',
          'NMRA MEC evaluates quality, safety, efficacy and need',
          'Marketing authorisation issued after approval',
          'Registrations valid for defined period (e.g., 5 years)',
          'Variation/renewal must be submitted before expiry'
        ],
        insight: 'Clear regulatory framework with defined processes and increasing alignment to global standards. English language acceptance simplifies documentation for Indian exporters.'
      },
      nutra: {
        timeline: 'Depends on classification outcome and documentation completeness',
        requirements: [
          'Borderline category classification (food + medicine traits) via NMRA',
          'Preliminary Classification Report (valid 1 year) required before submission',
          'Full qualitative/quantitative formula with COAs',
          'GMP/QMS certificates for manufacturing site',
          'Label & claims compliance with Food (Labeling & Advertising) Regulation 2005',
          'Import licence required',
          'Safety data for novel/high dose ingredients',
          'Correct claims, ingredient statement, nutrition information'
        ],
        insight: 'Supplements fall under borderline category and require classification before submission. Defined classification process with increasing consumer awareness of wellness products.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in Sri Lanka',
      steps: [
        'Manufacturing site approval or import-licence setup for foreign manufacturer',
        'Marketing Authorisation Application (MAA) submission via Form A (Schedule IV)',
        'Dossier submission with samples - Manufacturing licence, GMP certificate',
        'Technical dossier content - COA, stability data, analytical methods',
        'Clinical/efficacy summaries where required',
        'NMRA MEC Evaluation - Quality, safety, efficacy and need assessment',
        'Query handling and response management',
        'Marketing authorisation issuance',
        'Registration validity (e.g., 5 years) with renewal requirements',
        'Post-market monitoring and variation submissions'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical / Food Supplement Registration',
      steps: [
        'Preliminary Classification - Obtain borderline product classification from NMRA',
        'Classification Report validity - Valid for 1 year',
        'Full formula preparation - Qualitative/quantitative with COAs',
        'GMP/QMS certification - For manufacturing site',
        'Import licence setup - Required for foreign products',
        'Label artwork preparation - Comply with Food (Labeling & Advertising) Regulation 2005',
        'Claims compliance - Correct claims, ingredients, nutrition info',
        'Safety justification - For novel/high dose ingredients',
        'NMRA dossier submission',
        'Approval and registration issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'Application form (Form A, Schedule IV)',
        'Manufacturing/Import licence',
        'GMP certificate',
        'CTD/technical dossier with quality, stability',
        'COA, analytical methods & validation',
        'Samples (for import clearance)',
        'Labelling/inserts',
        'Clinical/efficacy summaries where required'
      ],
      'Supplements': [
        'Preliminary Classification Report (valid 1 year)',
        'Full composition + COA',
        'GMP certificate of manufacturer',
        'Label artwork complying with advertising & claims rules',
        'Imported product documentation (CFS, Free Sale)',
        'Safety justification for novel/high dose ingredients',
        'Compliance with Food (Labeling & Advertising) Regulation 2005'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Sri Lanka Should Be in Your Asia Expansion Map',
      reasons: [
        'Clear regulatory framework (NMRA) with defined processes',
        'Increasing alignment to global standards',
        'Import-oriented finished product market (limited local manufacturing)',
        'Transitioning disease burden: NCDs growing rapidly',
        'Supports chronic therapy and nutraceutical demand',
        'English language acceptance simplifies documentation',
        'Defined classification for nutraceuticals',
        'Increasing consumer awareness of wellness products'
      ],
      bestPortfolio: [
        'Pharma: Antihypertensives, antidiabetics, lipid-lowering agents, paediatric antibiotics, essential generics',
        'Nutra: Metabolic support, cardiovascular wellness, prenatal & women\'s health, immune/vitamin blends'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Sri Lanka',
      services: [
        'Market Fit & Portfolio Strategy - Align products with Sri Lanka\'s healthcare demand',
        'Dossier Preparation & Local Representation - Build NMRA-compliant dossiers',
        'Supplement Classification & Compliance - Guide classification, composition, COA, label claims',
        'Label & Claims Review - Ensure Food Labeling & Advertising regulations compliance',
        'Query & Lifecycle Management - Handle NMRA queries, renewals, post-market obligations',
        'Import Licence Maintenance - Support local agent/MAH coordination'
      ]
    },
    
    faqs: [
      {
        question: 'Which authority regulates medicines in Sri Lanka?',
        answer: 'The National Medicines Regulatory Authority (NMRA) under the NMRA Act No. 05 of 2015 regulates medicinal products including importation, manufacture, distribution and registration.'
      },
      {
        question: 'Are health supplements regulated in Sri Lanka?',
        answer: 'Yes. Supplements/food-based "borderline products" are regulated by NMRA and the Ministry\'s Food Control Administration Unit. They require classification and registration.'
      },
      {
        question: 'What dossier format is required for pharmaceuticals?',
        answer: 'Dossiers follow the requirements laid out by NMRA (Form A / Schedule IV) and require GMP, COA, stability, etc. Separate guidelines apply for generics vs new chemical entities.'
      },
      {
        question: 'Are Indian GMP certificates accepted?',
        answer: 'Yes, provided the manufacturer complies with GMP standards recognised by NMRA and has import licence and local agent.'
      },
      {
        question: 'What languages must packaging use?',
        answer: 'While English is widely used, import packaging should accommodate local languages (Sinhala/Tamil) depending on distribution, and must meet labelling standards set under advertising/food regulations.'
      }
    ]
  };

  return <CountryTemplate countryData={sriLankaData} />;
};
